import { useEffect, useState } from "react";
import { Check, LogOut, Star, X as XIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = ["pending", "approved", "rejected"];

const Stars = ({ value }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} strokeWidth={1.5} className={`h-4 w-4 ${n <= value ? "fill-[#F97316] text-[#F97316]" : "text-white/25"}`} />
    ))}
  </div>
);

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("poc_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(false);

  const logout = () => {
    localStorage.removeItem("poc_admin_token");
    setToken("");
    setReviews([]);
  };

  const fetchReviews = async (t, tok) => {
    if (!tok) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/reviews?status=${t}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401) {
        logout();
        return;
      }
      setReviews(await res.json());
    } catch {
      toast.error("Could not load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews(tab, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem("poc_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.message);
    }
  };

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/admin/reviews/${id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Review ${status}`);
      fetchReviews(tab, token);
    } catch {
      toast.error("Action failed");
    }
  };

  const field =
    "w-full border-b border-white/15 bg-transparent py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#F97316]";

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#111111] p-8 md:p-10">
          <img src="/assets/logo-white.png" alt="Payung Oren Creative" className="h-8 w-auto" />
          <h1 className="mt-8 font-syne text-2xl font-bold tracking-tight">Admin Login</h1>
          <p className="mt-2 font-spacemono text-[10px] uppercase tracking-[0.25em] text-white/35">
            Review moderation dashboard
          </p>
          <form onSubmit={login} className="mt-8 flex flex-col" data-testid="admin-login-form">
            <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="admin-email-input" />
            <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={`${field} mt-5`} data-testid="admin-password-input" />
            {error && (
              <p className="mt-4 text-sm text-red-400" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              className="mt-8 rounded-full bg-[#F97316] px-8 py-3.5 font-spacemono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#EA580C]"
              data-testid="admin-login-submit"
            >
              Sign In
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen max-w-5xl px-6 pb-24 pt-32 md:pt-36" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="font-spacemono text-xs text-[#F97316]">(Admin)</p>
          <h1 className="mt-3 font-syne text-3xl font-bold tracking-tight md:text-4xl">Review Moderation</h1>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 font-spacemono text-[10px] uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:border-[#F97316] hover:text-white"
          data-testid="admin-logout-button"
        >
          <LogOut className="h-3.5 w-3.5" /> Logout
        </button>
      </div>

      <div className="mt-10 flex gap-2" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-6 py-2.5 font-spacemono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === t ? "bg-[#F97316] text-white" : "border border-white/15 text-white/50 hover:text-white"
            }`}
            data-testid={`admin-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-4">
        {loading && <p className="font-spacemono text-xs uppercase tracking-[0.25em] text-white/35">Loading…</p>}
        {!loading && reviews.length === 0 && (
          <p className="rounded-2xl border border-white/10 bg-[#111111] p-10 text-center font-spacemono text-[11px] uppercase tracking-[0.25em] text-white/35" data-testid="admin-empty-state">
            No {tab} reviews
          </p>
        )}
        {reviews.map((r) => (
          <div key={r.id} className="rounded-2xl border border-white/10 bg-[#111111] p-7 md:p-8" data-testid={`admin-review-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <Stars value={r.rating} />
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/70 md:text-base">&ldquo;{r.review}&rdquo;</p>
              </div>
              <div className="flex gap-2">
                {r.status !== "approved" && (
                  <button
                    onClick={() => setStatus(r.id, "approved")}
                    className="flex items-center gap-2 rounded-full bg-[#F97316] px-5 py-2.5 font-spacemono text-[10px] uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-[#EA580C]"
                    data-testid={`approve-button-${r.id}`}
                  >
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button
                    onClick={() => setStatus(r.id, "rejected")}
                    className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-spacemono text-[10px] uppercase tracking-[0.15em] text-white/60 transition-colors duration-300 hover:border-red-500 hover:text-red-400"
                    data-testid={`reject-button-${r.id}`}
                  >
                    <XIcon className="h-3.5 w-3.5" /> Reject
                  </button>
                )}
              </div>
            </div>
            <div className="mt-5 border-t border-white/10 pt-4 font-spacemono text-[10px] uppercase tracking-[0.2em] text-white/40">
              {r.name} — {r.position}, {r.company} · {r.email} ·{" "}
              {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
